<script setup>
//#region imports
  import { ref, computed, nextTick, onMounted } from 'vue';
  import { get, differenceWith, debounce, filter } from 'lodash-es';
  import { useI18n } from 'vue-i18n';
  import { useError } from '@/composables/useError';

//#endregion

//#region props
  const props = defineProps({
    modelValue: {
      type: [Object, String, Array, Number, Boolean, null, undefined],
      default() {
        return null;
      },
    },
    label: {
      type: String,
      default() {
        return '';
      },
    },
    labelSize: {
      type: Number,
      default() {
        return 14;
      },
    },
    labelBold: {
      type: Boolean,
      default() {
        return false;
      },
    },
    disableOptions: {
      type: Boolean,
      default() {
        return true;
      },
    },
    placeholder: {
      type: String,
      default() {
        return '';
      },
    },
    size: {
      type: String,
      default() {
        return 'medium';
      },
    },
    ellipsis: {
      type: Boolean,
      default() {
        return false;
      },
    },
    filterable: {
      type: Boolean,
      default() {
        return false;
      },
    },
    disabled: {
      type: Boolean,
      default() {
        return false;
      },
    },
    readonly: {
      type: Boolean,
      default() {
        return false;
      },
    },
    optionNameParam: {
      type: String,
      default() {
        return 'name';
      },
    },
    optionLabelParam: {
      type: String,
      default() {
        return 'name';
      },
    },
    optionValueParam: {
      type: String,
      default() {
        return 'id';
      },
    },
    isValueObject: {
      type: Boolean,
      default() {
        return false;
      },
    },
    valueKey: {
      type: String,
      default() {
        return '';
      },
    },
    list: {
      type: [Array, Object],
      default() {
        return [];
      },
    },
    filterMethod: {
      type: Function,
    },
    error: {
      type: [String, Number, Array],
      default() {
        return '';
      },
    },
    prependIcon: {
      type: String,
      default() {
        return '';
      },
    },
    prependText: {
      type: String,
      default() {
        return '';
      },
    },
    popperClass: {
      type: String,
      default() {
        return '';
      },
    },
    loading: {
      type: Boolean,
      default() {
        return false;
      },
    },
    remote: {
      type: Boolean,
      default() {
        return false;
      },
    },
    remoteMethod: {
      type: Function,
    },
    required: {
      type: Boolean,
      default() {
        return false;
      },
    },
    isError: {
      type: Boolean,
      default() {
        return false;
      },
    },
    clearable: {
      type: Boolean,
      default() {
        return false;
      },
    },
    multiple: {
      type: Boolean,
      default() {
        return false;
      },
    },
    teleported: {
      type: Boolean,
      default() {
        return true;
      },
    },
    selectItemLogo: {
      type: String,
      default() {
        return '';
      },
    },
    listItemLogo: {
      type: String,
      default() {
        return '';
      },
    },
    width: {
      type: [String, Number],
      default() {
        return '';
      },
    },
    isVirtual: {
      type: Boolean,
      default() {
        return false;
      },
    },
    collapseTagTooltip: {
      type: Boolean,
      default() {
        return false;
      },
    },
    maxCollapseTags: {
      type: Number,
      default() {
        return 1;
      },
    },
    searchParam: {
      type: String,
      default() {
        return '';
      },
    },
    isGrouped: {
      type: Boolean,
      default() {
        return false;
      },
    },
    noScroll: {
      type: Boolean,
      default() {
        return false;
      },
    },
    fitDropdownWidth: {
      type: Boolean,
      default() {
        return false;
      },
    },
    isDynamic: {
      type: Boolean,
      default() {
        return false;
      },
    },
    excludeOptions: {
      type: Array,
      default() {
        return null;
      },
    },
  });
//#endregion

//#region emits
  const emit = defineEmits(['focus', 'update:model-value', 'visible-change', 'blur', 'change']);
//#endregion

//#region data
  const searchQuery = ref('');
  const focused = ref(false);
  const customMultipleSearchRef = ref(null);
  const uiSelectElement = ref(null);
  const { currentError } = useError({
    error: computed(() => {
      return props.error;
    }),
  });
  const { t } = useI18n();
//#endregion

//#region computed
  const selectFilterMethod = computed(() => {
    if (!props.filterable || props.remote || !props.searchParam) {
      return undefined;
    }

    return (query) => {
      searchQuery.value = query;
    };
  });
  const normalizedSearchQuery = computed(() => searchQuery.value.trim().toLowerCase());
  const isMultipleSearch = computed(() => props.filterable && props.multiple);
  const filteredOptions = computed(() => {
    if (!props.excludeOptions && (!props.filterable || props.remote || !props.searchParam || normalizedSearchQuery.value === '')) {
      return props.list;
    }

    const matcher = (item) => get(item, props.searchParam, '').toLowerCase().includes(normalizedSearchQuery.value);

    if (props.isGrouped) {
      return props.list.reduce((acc, group) => {
        const options = get(group, 'options', []).filter(matcher);

        if (options.length) {
          acc.push({
            ...group,
            options,
          });
        }

        return acc;
      }, []);
    }
    let result = filter(props.list, matcher);
    if(props.excludeOptions) {
      const excludeOptions = props.excludeOptions.filter((item) => item !== props.modelValue);
      result = differenceWith(
        result,
        excludeOptions,
        (originalItem, excludeItem) => originalItem[props.optionValueParam] === excludeItem,
      );
    }
    return result;
  });

  const pPopperClass = computed(() => {
    const classes = ['ui-select-element__dropdown', props.popperClass];

    if (props.ellipsis) {
      classes.push('ui-select-element__dropdown_ellipsis');
    }
    if (props.noScroll) {
      classes.push('ui-select-element__dropdown_no-scroll');
    }
    if (props.fitDropdownWidth) {
      classes.push('ui-select-element__dropdown_fit-width');
    }

    return classes.join(' ');
  });
  const pWidth = computed(() => {
    return props.width === '' ? '100%' : `${props.width}px`;
  });
  const pMinWidth = computed(() => {
    return props.width === '' ? '' : `${props.width}px`;
  });
  const pPlaceholder = computed(() => {
    return props.placeholder === '' ? t('ui.select.default') : props.placeholder;
  });
  const select = computed(() => {
    return props.isVirtual ? 'el-select-v2' : 'el-select';
  });
//#endregion

//#region methods
  const handleFocus = (event) => {
    focused.value = true;
    emit('focus', event);
  };

  const handleBlur = (event) => {
    focused.value = false;
    emit('blur', event);
  };

  const handleChange = (value) => {
    emit('change', value);
    if (props.isDynamic) {
      adjustSize();
    }

    if (!props.filterable) {
      return;
    }

    searchQuery.value = '';
    if (uiSelectElement.value?.states) {
      uiSelectElement.value.states.inputValue = '';
    }
  };

  const handleVisibleChange = async (value) => {
    emit('visible-change', value);

    if (!value) {
      searchQuery.value = '';
    } else {
      if(isMultipleSearch.value) {
        await nextTick();
        customMultipleSearchRef.value.focus();
      }
    }
  };
  const adjustSize = async () => {
    await nextTick();
    uiSelectElement.value.$el.querySelector('.el-select__selection').style.width = `${uiSelectElement.value.$el.querySelector('.el-select__placeholder span').getBoundingClientRect().width + 5}px`;
  };
//#endregion
const customMultipleSearch = debounce(() => {
  if (props.remoteMethod) {
    props.remoteMethod(searchQuery.value);
  } else {
    if (typeof uiSelectElement.value.onInput === 'function') {
      //uiSelectElement.value.debouncedOnInputChange(searchQuery.value);
      uiSelectElement.value.onInput({
        target: {
          value: searchQuery.value,
        },
      });
    }
  }
}, 600);

onMounted(async () => {
  await nextTick();
  if(props.remote && props.modelValue) {
    uiSelectElement.value.remoteMethod();
  }
  if (props.isDynamic) {
    adjustSize();
  }
});

defineExpose({
  uiSelectElement,
});
</script>

<template>
  <div
    :class="[
      'ui-select-element',
      `ui-select-element_${size}`,
      {
        'ui-select-element_focused': focused,
        'ui-select-element_disabled': disabled,
        'ui-select-element_readonly': readonly,
        'ui-select-element_error': currentError || isError,
        'ui-select-element_hide-search': isMultipleSearch
      }
    ]"
    :style="{ width: pWidth, minWidth: pMinWidth }"
  >
    <UiLabel
      v-if="label"
      :font-size="labelSize"
      :bold="labelBold"
      :required="required"
      :label="label"
      :disabled="disabled"
      class="t:mb-8"
    />
    <div class="ui-select-element__wrapper">
      <component
        :is="select"
        ref="uiSelectElement"
        class="ui-select-element__element"
        :model-value="modelValue"
        :placeholder="pPlaceholder"
        :filterable="filterable"
        :remote="remote"
        :remote-method="remoteMethod"
        :filter-method="selectFilterMethod"
        :max-collapse-tags="maxCollapseTags"
        :multiple="multiple"
        :clearable="clearable"
        :disabled="disabled || readonly"
        collapse-tags
        remote-show-suffix
        :teleported="teleported"
        :popper-class="pPopperClass"
        :collapse-tags-tooltip="collapseTagTooltip"
        :loading="loading"
        :fallback-placements="['bottom-start', 'top-start', 'bottom', 'top']"
        :value-key="valueKey"
        :reserve-keyword="false"
        v-bind="isVirtual ? {
          options: list,
          props: {
            label: optionLabelParam,
            value: optionValueParam,
          }
        } : {}"
        @update:model-value="$emit('update:model-value', $event)"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        @visible-change="handleVisibleChange"
      >
        <template
          v-if="isMultipleSearch"
          v-slot:header
        >
          <UiInput
            ref="customMultipleSearchRef"
            v-model="searchQuery"
            :placeholder="t('ui.select.filter_option')"
            clearable
            autosize
            @change="customMultipleSearch"
          />
        </template>
        <template
          v-if="selectItemLogo || prependIcon !== '' || prependText !== '' || $slots.customPrefix"
          v-slot:prefix
        >
          <img
            v-if="selectItemLogo"
            :src="selectItemLogo"
          >
          <UiIcon
            v-if="prependIcon !== ''"
            color="var(--text-body-tertiary)"
            :name="prependIcon"
          />
          <div
            v-if="prependText"
            class="ui-select-element__element-prepend-text"
          >
            {{ prependText }}
          </div>
          <slot name="customPrefix" />
        </template>
        <template v-slot:empty>
          <div class="ui-select-element__no-data">
            {{ $t('players.info.no_data') }}
          </div>
        </template>
        <template v-slot:loading>
          <div class="ui-select-element__loading">
            Loading
          </div>
        </template>
        <slot
          v-if="!isVirtual"
          name="options"
        >
          <template v-if="isGrouped">
            <ElOptionGroup
              v-for="group in filteredOptions"
              :key="group.label"
              :label="group.label"
            >
              <ElOption
                v-for="item in group.options"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </ElOptionGroup>
          </template>
          <template v-else>
            <ElOption
              v-for="item in filteredOptions"
              :key="item[optionValueParam]"
              class="ui-select-element__list-item"
              :label="item[optionLabelParam]"
              :value="isValueObject ? item : item[optionValueParam] || ''"
              :disabled="props.disableOptions ? item.is_disabled : false"
            >
              <img
                v-if="item[listItemLogo]"
                class="ui-select-element__list-item-logo"
                :src="item[listItemLogo]"
              >
              <span>{{ item[optionNameParam] }}</span>
            </ElOption>
          </template>
        </slot>
      </component>
    </div>
    <UiHelp
      v-if="currentError"
      is-error
      :text="currentError"
    />
  </div>
</template>

<style lang='scss'>
.ui-select-element {
  &__wrapper {
    display: flex;
    align-items: center;
    border: 1px solid var(--border-default);
    background-color: var(--background-body);
    background-image: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.05) 100%);
    transition: all 0.2s;
    border-radius: 5px;
    position: relative;
  }
  &__element {
    cursor: pointer;
    .el-select__wrapper{
      width: 100%;
      align-items: center;
      background: transparent;
      border: none;
      display: flex;
      flex: 10 1 auto;
      height: 100%;
      max-width: 100%;
      min-width: 10%;
      outline: none;
      box-shadow: none;
      min-height: auto;
      &.is-hovering:not(.is-focused){
        box-shadow: none;
      }

      .el-select__selected-item{
        color: var(--text-body);
        font-weight: 400;
        line-height: normal;

        &.is-transparent {
          opacity: 0.3;
        }
      }
    }
    .el-select__input{
      height: 20px;
    }
    .el-select {
      svg {
        display: none;
      }
      &__caret {
        &:after{
          font-family: "Font Awesome 7 Pro";
          content: "\f107";
          font-style: normal;
        }
      }
      &__clear {
        &:after{
          font-family: "Font Awesome 7 Pro";
          content: "\f057";
          font-weight: 900;
          font-style: normal;
        }
      }
      &__selection {
        &.is-near {
          margin-left: inherit;
        }
      }
    }
  }
  &__element-prepend-text {
    font-size: 12px;
  }

  &__dropdown {
    &.el-tooltip.el-popper{
      box-shadow: var(--el-box-shadow-light) !important;
    }
    z-index: 90000 !important;
    max-width: 600px;

    &_fit-width {
      max-width: none;
    }

    .el-select-dropdown .el-select-dropdown__item.is-selected{
      color: var(--text-primary);
      &:after{
        width: 12px;
        right: 12px;
        mask: none;
        content: "\f00c";
        font-family: "Font Awesome 7 Pro";
        font-weight: 900;
        background: transparent;
        position: absolute;
      }
    }
    .el-select-group__title{
      padding-left: 8px;
      font-size: 12px;
      color: var(--text-primary);
      font-weight: 600;
    }
    .el-select-dropdown__item{
      font-size: 13px;
      color: var(--text-body);
      height: 30px !important;
      line-height: 1;
      padding: 8px 26px 8px 12px;
      &.is-disabled {
        color: var(--text-body-tertiary);
        cursor: not-allowed;
      }
      &:hover {
        background: var(--background-brand-subtle);
      }
    }

    .ui-select-element__loading,
    .ui-select-element__no-data{
      color: var(--text-body-tertiary);
      font-size: 14px;
    }

    &_ellipsis {
      .el-select-dropdown__item{
        display: flex;
        text-overflow: ellipsis;
        max-width: 400px!important;
        span {
          text-overflow: ellipsis;
          width: 100%;
          overflow: hidden;
        }
      }
    }
    &_no-scroll{
      .el-select-dropdown {
        .el-select-dropdown__wrap{
          max-height: 100%;
        }
        .el-select-group__wrap{
          padding-bottom: 10px;
          &:after{
            content: none;
          }
        }
      }
    }
  }
  &__list-item{
    display: flex;
    align-items: center;
  }
  &__list-item-logo{
    width: 16px;
    height: 16px;
    margin-right: 4px;
    border-radius: 4px;
  }
  &_hide-search {
    .el-select__input-wrapper {
      display: none;
    }
  }
  &_focused & {
    &__wrapper {
      border-color: var(--border-focused);
    }
  }
  &_error & {
    &__wrapper {
      border-color: var(--border-negative);
    }
  }
  &_disabled {
    cursor: not-allowed;
    .el-select{
      cursor: not-allowed;
    }
    .el-select__wrapper {
      pointer-events: none;
      opacity: 0.5;
    }
  }
  &_readonly {
    cursor: not-allowed;
    .el-select{
      cursor: not-allowed;
    }
    .el-select__wrapper {
      pointer-events: none;
      opacity: 0.5;
    }
  }
  &_disabled & {
    &__wrapper {
      opacity: 0.5;
      pointer-events: none;
      .el-select__caret {
        cursor: not-allowed;
      }
    }
  }
  &_medium {
    .el-select__wrapper{
      padding:0 8px;
    }
    .el-select__selection {
      height: 28px;
    }
    .el-select__selected-item{
      font-size: 12px;
    }
  }
  &_big {
    .el-select__wrapper{
      padding:0 12px;
    }
    .el-select__selection {
      height: 32px;
    }
    .el-select__selected-item{
      font-size: 13px;
    }
  }
}

.ui-select-element__element .el-tag,
.ui-select-element__dropdown .el-tag{
  border-radius: 4px;
  border: 1px solid var(--border-default);
  background: var(--background-body);
  color: var(--text-body);
  font-size: 12px;
  font-weight: 500;
  padding: 0 6px;
  width: auto;
  height: 22px;
  display: flex;
  align-items: center;

  &__close{
    border-radius: 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    outline: none;
    line-height: 24px;
    color: var(--text-negative);
    background-color: transparent;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    path {
      stroke-width: 90px;
      stroke: var(--text-negative);
    }
    &:hover{
      color: var(--text-negative);
      background-color: transparent;
    }
  }
}
</style>
